import { db, getDeviceId } from "../db/database";
import type { AuditLog, PendingSync, VariantAlias } from "../types";
import { normalizeText } from "../utils/normalize";
import { randomId } from "../utils/id";
import { getSession } from "./auth";

async function identity() {
  const session = await getSession();
  return session?.user ?? { id: "offline-user", name: "Usuário offline" };
}

export async function addVariantAlias(variantId: string, aliasText: string): Promise<VariantAlias> {
  const alias = aliasText.trim();
  const normalizedAlias = normalizeText(alias);
  if (!normalizedAlias) throw new Error("Informe um nome alternativo válido.");

  const variant = await db.variants.get(variantId);
  if (!variant) throw new Error("Cor não encontrada.");
  const product = await db.products.get(variant.productId);
  if (!product) throw new Error("Produto não encontrado.");
  if (normalizeText(variant.colorName) === normalizedAlias) throw new Error("Esse já é o nome principal da cor.");

  const existing = await db.variantAliases.where("[productId+normalizedAlias]").equals([product.id, normalizedAlias]).first();
  if (existing) {
    if (existing.variantId === variantId) throw new Error("Esse nome alternativo já está cadastrado nesta cor.");
    throw new Error(`Esse nome alternativo já aponta para outra cor do SKU ${product.sku}.`);
  }

  const timestamp = new Date().toISOString();
  const user = await identity();
  const deviceId = await getDeviceId();
  const entry: VariantAlias = {
    id: randomId("alias"), productId: product.id, variantId, alias, normalizedAlias,
    kind: normalizeText(alias).startsWith(normalizeText(product.sku)) ? "sku" : "cor",
    source: "manual", createdAt: timestamp, updatedAt: timestamp,
  };
  const operation: PendingSync = {
    id: randomId("sync"), operationType: "product_upsert", payload: { product, aliases: [entry] },
    deviceId, userId: user.id, status: "pendente", attempts: 0, createdAt: timestamp, updatedAt: timestamp,
  };
  const audit: AuditLog = {
    id: randomId("audit"), action: "Nome alternativo cadastrado", entityType: "variant_alias", entityId: entry.id,
    after: entry, userId: user.id, userName: user.name, deviceId, createdAt: timestamp,
  };
  await db.transaction("rw", [db.variantAliases, db.pendingSync, db.auditLogs], async () => {
    await db.variantAliases.add(entry);
    await db.pendingSync.add(operation);
    await db.auditLogs.add(audit);
  });
  return entry;
}

export async function removeVariantAlias(aliasId: string): Promise<void> {
  const entry = await db.variantAliases.get(aliasId);
  if (!entry) return;
  const product = await db.products.get(entry.productId);
  if (!product) throw new Error("Produto não encontrado.");
  const timestamp = new Date().toISOString();
  const user = await identity();
  const deviceId = await getDeviceId();
  await db.transaction("rw", [db.variantAliases, db.pendingSync, db.auditLogs], async () => {
    await db.variantAliases.delete(aliasId);
    await db.pendingSync.add({
      id: randomId("sync"), operationType: "product_upsert", payload: { product, deletedAliasIds: [aliasId] },
      deviceId, userId: user.id, status: "pendente", attempts: 0, createdAt: timestamp, updatedAt: timestamp,
    });
    await db.auditLogs.add({
      id: randomId("audit"), action: "Nome alternativo removido", entityType: "variant_alias", entityId: aliasId,
      before: entry, userId: user.id, userName: user.name, deviceId, createdAt: timestamp,
    });
  });
}
